import axios from 'axios';
import {
  buscarFilaVendasUrbanas,
  removerVendaUrbanaDaFila,
  salvarMapeamentoVendaUrbanaSync,
  salvarVendaUrbanaOffline
} from './offlineStorage';
import { RESPONSE_ERROR_HANDLER_POSTPONE_HEADER } from './responseUtil';
import { URL } from './ambienteActions';
import { getAuthentication } from '../security/securityContext';
import { isUserAuthenticated } from '../actions/util/authenticationJwtActions';

const SYNC_CONFIG = {
  headers: {
    [RESPONSE_ERROR_HANDLER_POSTPONE_HEADER]: true
  }
}

const MAX_TENTATIVAS_POLLING = 360; // 360 × 10s = 1 hora

let sincronizando = false;
const pollingEmAndamento = new Set();

export async function sincronizarVendasUrbanasOffline(dispatch) {
  if (sincronizando || !navigator.onLine || !isUserAuthenticated(getAuthentication())) return;

  let fila;
  try {
    fila = await buscarFilaVendasUrbanas();
  } catch (e) {
    return;
  }

  if (!fila || fila.length === 0) return;

  sincronizando = true;
  try {
    for (const venda of fila) {
      if (venda.erroSync) continue;
      if (venda.statusServidor === 'PROCESSANDO') {
        // timeout pode ter sido perdido em reload de página — reinicia polling, se não houver um já em andamento
        if (!pollingEmAndamento.has(venda.idChaveOffline)) {
          agendarPollingStatus(venda.idChaveOffline, venda, dispatch, 0);
        }
        continue;
      }
      await enviarVenda(venda, dispatch);
    }
  } finally {
    sincronizando = false;
  }
}

async function enviarVenda(venda, dispatch) {
  try {
    const response = await axios.post(`${URL}/vendas-urbanas/venda-urbana-offline`, {
      idChaveOffline: venda.idChaveOffline,
      dadosVenda: venda.dadosVenda
    }, SYNC_CONFIG);

    const { idChaveOffline } = response.data;

    await salvarVendaUrbanaOffline({ ...venda, statusServidor: 'PROCESSANDO' });

    agendarPollingStatus(idChaveOffline, venda, dispatch, 0);

  } catch (erro) {
    if (!erro.response) return;
    const status = erro.response.status;
    if (status === 401) return; // sessão expirada: tenta de novo após reautenticação
    if (status >= 400 && status < 500) {
      const violacoes = erro.response.data && erro.response.data.violacoes;
      const mensagemErro = violacoes && violacoes.length
        ? violacoes.map(v => v.mensagem).join(', ')
        : 'Erro HTTP ' + status;
      await salvarVendaUrbanaOffline({ ...venda, erroSync: mensagemErro, erroSyncStatus: status });
    }
  }
}

function agendarPollingStatus(idChaveOffline, venda, dispatch, tentativa) {
  if (tentativa === 0) {
    pollingEmAndamento.add(idChaveOffline);
  }

  if (tentativa >= MAX_TENTATIVAS_POLLING) {
    pollingEmAndamento.delete(idChaveOffline);
    salvarVendaUrbanaOffline({ ...venda, erroSync: 'Timeout aguardando processamento no servidor' });
    return;
  }

  setTimeout(async () => {
    try {
      const response = await axios.get(
        `${URL}/vendas-urbanas/venda-urbana-offline/${idChaveOffline}/status`,
        SYNC_CONFIG
      );
      const { status, vendaUrbanaId, erroMensagem } = response.data;

      if (status === 'RESOLVIDO') {
        pollingEmAndamento.delete(idChaveOffline);
        salvarMapeamentoVendaUrbanaSync(idChaveOffline, vendaUrbanaId);
        await removerVendaUrbanaDaFila(idChaveOffline);
        if (dispatch) dispatch({ type: 'VENDAS_URBANAS_OFFLINE_ENVIADAS', payload: idChaveOffline });

      } else if (status === 'ERRO_SYNC') {
        pollingEmAndamento.delete(idChaveOffline);
        await salvarVendaUrbanaOffline({ ...venda, erroSync: erroMensagem || 'Erro no processamento' });

      } else {
        agendarPollingStatus(idChaveOffline, venda, dispatch, tentativa + 1);
      }
    } catch (erro) {
      if (erro.response && erro.response.status === 401) {
        pollingEmAndamento.delete(idChaveOffline);
        return; // sessão expirada: a próxima sincronização retoma o polling
      }
      agendarPollingStatus(idChaveOffline, venda, dispatch, tentativa + 1);
    }
  }, 10 * 1000);
}

export function inicializarEventosSyncOfflineVendaUrbana(dispatch) {
  window.addEventListener('online', () => sincronizarVendasUrbanasOffline(dispatch));

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      sincronizarVendasUrbanasOffline(dispatch);
    }
  });

  window.addEventListener('pageshow', () => sincronizarVendasUrbanasOffline(dispatch));
  window.addEventListener('focus', () => sincronizarVendasUrbanasOffline(dispatch));

  setInterval(() => sincronizarVendasUrbanasOffline(dispatch), 30000);

  sincronizarVendasUrbanasOffline(dispatch);
}
